"use client";

import type React from "react";
import { useState, useEffect } from "react";
import useAdminApiHook from "../../../../utils/useAdminApi";
import {
  Mail,
  User,
  Lock,
  Phone,
  CheckCircle,
  AlertCircle,
  Loader,
  Users,
  ChevronDown,
} from "lucide-react";
import { toast } from "react-toastify";

// Interface for role data
interface Role {
  roleName: string;
  roleCode: string;
  roleDescription: string;
  roleReference: string;
  parentReference: string | null;
  merchantRef: string | null;
  merchantId: number | null;
  merchantName: string | null;
  isAdminRole: boolean;
  isSystemRole: boolean;
  isCustomRole: boolean;
  isMerchantRole: boolean;
  roleStatus: string;
  rolePermissions: string[];
  adminRolePermissions: string[];
}

// Interface for API response
interface RoleApiResponse {
  statusCode: string;
  status: string;
  description: string;
  timestamp: string | null;
  errorCode: string;
  data: {
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
    data: Role[];
  };
}

const AdminProfileForm = () => {
  // Form state
  const [formData, setFormData] = useState({
    emailAddress: "",
    lastName: "",
    firstName: "",
    password: "",
    phoneNumber: "",
    roleReference: "",
  });

  // Validation state
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");

  // API hook for fetching roles
  const {
    data: rolesData,
    isLoading: isLoadingRoles,
    makeRequest: fetchRoles,
  } = useAdminApiHook<RoleApiResponse>("/admin/roles", {
    method: "get",
  });

  // API hook for creating profile
  const { isLoading, makeRequest } = useAdminApiHook(
    `/admin/system/admin-profile`,
    {
      method: "post",
      allowSystemAdmin: true,
    }
  );

  // Fetch roles on component mount
  useEffect(() => {
    fetchRoles({ page: 1, perPage: 100 }); // Get all roles at once
  }, []);

  // Filter roles to only show admin roles
  const adminRoles =
    rolesData?.data?.data?.filter(
      (role) => role.isAdminRole && role.adminRolePermissions.length > 0
    ) || [];

  // Handle input changes
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user types
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Check for empty fields
    Object.entries(formData).forEach(([key, value]) => {
      if (!value.trim()) {
        newErrors[key] = "This field cannot be empty";
      }
    });

    // Email validation
    if (
      formData.emailAddress &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailAddress)
    ) {
      newErrors.emailAddress = "Please enter a valid email address";
    }

    // Phone validation
    if (
      formData.phoneNumber &&
      !/^\d{10,15}$/.test(formData.phoneNumber.replace(/[^0-9]/g, ""))
    ) {
      newErrors.phoneNumber = "Please enter a valid phone number";
    }

    // Password validation
    if (formData.password && formData.password.length < 10) {
      newErrors.password = "Password must be at least 10 characters long";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check if all fields are filled
  const isFormValid = () => {
    return (
      Object.values(formData).every((value) => value.trim() !== "") &&
      Object.keys(errors).length === 0
    );
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const response = await makeRequest(formData);
      console.log(response);

      if (response?.status === 200 || response?.status === 201) {
        setSubmitStatus("success");
        toast("Admin has been profiled successfully");

        setTimeout(() => {
          window.location.reload();
        }, 4000);

        // Reset form after successful submission
        setTimeout(() => {
          setFormData({
            emailAddress: "",
            lastName: "",
            firstName: "",
            password: "",
            phoneNumber: "",
            roleReference: "",
          });
          setSubmitStatus("idle");
        }, 3000);
      } else {
        setSubmitStatus("error");
        setTimeout(() => setSubmitStatus("idle"), 3000);
      }
    } catch (error) {
      setSubmitStatus("error");
      setTimeout(() => setSubmitStatus("idle"), 3000);
    }
  };

  // Get selected role details for display
  const selectedRole = adminRoles.find(
    (role) => role.roleReference === formData.roleReference
  );

  return (
    <section className="max-w-2xl mx-auto p-6">
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6">
          <h2 className="text-2xl font-bold text-white">
            Create Admin Profile With Custom Role
          </h2>
          <p className="text-blue-100 mt-1">
            Please fill in all the required information and select a role
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Status message */}
          {submitStatus === "success" && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center text-green-700">
              <CheckCircle className="h-5 w-5 mr-2 text-green-500" />
              <span>Profile created successfully!</span>
            </div>
          )}

          {submitStatus === "error" && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center text-red-700">
              <AlertCircle className="h-5 w-5 mr-2 text-red-500" />
              <span>
                There was an error creating the profile. Please try again.
              </span>
            </div>
          )}

          {/* First Name */}
          <div>
            <label
              className="block text-sm font-medium text-gray-700 mb-1"
              htmlFor="firstName"
            >
              First Name
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className={`pl-10 w-full py-3 px-4 bg-gray-50 border rounded-lg focus:ring-2 focus:outline-none transition-colors ${
                  errors.firstName
                    ? "border-red-300 text-red-900 focus:border-red-500 focus:ring-red-200"
                    : "border-gray-300 focus:border-blue-500 focus:ring-blue-200"
                }`}
                placeholder="Enter your first name"
              />
            </div>
            {errors.firstName && (
              <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
            )}
          </div>

          {/* Last Name */}
          <div>
            <label
              className="block text-sm font-medium text-gray-700 mb-1"
              htmlFor="lastName"
            >
              Last Name
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className={`pl-10 w-full py-3 px-4 bg-gray-50 border rounded-lg focus:ring-2 focus:outline-none transition-colors ${
                  errors.lastName
                    ? "border-red-300 text-red-900 focus:border-red-500 focus:ring-red-200"
                    : "border-gray-300 focus:border-blue-500 focus:ring-blue-200"
                }`}
                placeholder="Enter your last name"
              />
            </div>
            {errors.lastName && (
              <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label
              className="block text-sm font-medium text-gray-700 mb-1"
              htmlFor="emailAddress"
            >
              Email Address
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="email"
                id="emailAddress"
                name="emailAddress"
                value={formData.emailAddress}
                onChange={handleChange}
                className={`pl-10 w-full py-3 px-4 bg-gray-50 border rounded-lg focus:ring-2 focus:outline-none transition-colors ${
                  errors.emailAddress
                    ? "border-red-300 text-red-900 focus:border-red-500 focus:ring-red-200"
                    : "border-gray-300 focus:border-blue-500 focus:ring-blue-200"
                }`}
                placeholder="Enter your email address"
              />
            </div>
            {errors.emailAddress && (
              <p className="mt-1 text-sm text-red-600">{errors.emailAddress}</p>
            )}
          </div>

          {/* Phone Number */}
          <div>
            <label
              className="block text-sm font-medium text-gray-700 mb-1"
              htmlFor="phoneNumber"
            >
              Phone Number
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Phone className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                className={`pl-10 w-full py-3 px-4 bg-gray-50 border rounded-lg focus:ring-2 focus:outline-none transition-colors ${
                  errors.phoneNumber
                    ? "border-red-300 text-red-900 focus:border-red-500 focus:ring-red-200"
                    : "border-gray-300 focus:border-blue-500 focus:ring-blue-200"
                }`}
                placeholder="Enter your phone number"
              />
            </div>
            {errors.phoneNumber && (
              <p className="mt-1 text-sm text-red-600">{errors.phoneNumber}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <label
              className="block text-sm font-medium text-gray-700 mb-1"
              htmlFor="password"
            >
              Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`pl-10 w-full py-3 px-4 bg-gray-50 border rounded-lg focus:ring-2 focus:outline-none transition-colors ${
                  errors.password
                    ? "border-red-300 text-red-900 focus:border-red-500 focus:ring-red-200"
                    : "border-gray-300 focus:border-blue-500 focus:ring-blue-200"
                }`}
                placeholder="Enter your password"
              />
            </div>
            {errors.password && (
              <p className="mt-1 text-sm text-red-600">{errors.password}</p>
            )}
          </div>

          {/* Role Selection - Simplified Dropdown */}
          <div>
            <label
              className="block text-sm font-medium text-gray-700 mb-1"
              htmlFor="roleReference"
            >
              Admin Role
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Users className="h-5 w-5 text-gray-400" />
              </div>
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <ChevronDown className="h-5 w-5 text-gray-400" />
              </div>
              <select
                id="roleReference"
                name="roleReference"
                value={formData.roleReference}
                onChange={handleChange}
                disabled={isLoadingRoles}
                className={`pl-10 pr-10 w-full py-3 px-4 bg-gray-50 border rounded-lg focus:ring-2 focus:outline-none transition-colors appearance-none ${
                  errors.roleReference
                    ? "border-red-300 text-red-900 focus:border-red-500 focus:ring-red-200"
                    : "border-gray-300 focus:border-blue-500 focus:ring-blue-200"
                } ${
                  isLoadingRoles
                    ? "cursor-not-allowed opacity-50"
                    : "cursor-pointer"
                }`}
              >
                <option value="">
                  {isLoadingRoles ? "Loading roles..." : "Select an admin role"}
                </option>
                {adminRoles.map((role) => (
                  <option key={role.roleReference} value={role.roleReference}>
                    {role.roleName}
                  </option>
                ))}
              </select>
              {isLoadingRoles && (
                <div className="absolute inset-y-0 right-8 flex items-center">
                  <Loader className="h-4 w-4 animate-spin text-gray-400" />
                </div>
              )}
            </div>

            {/* Show selected role permissions */}
            {selectedRole && (
              <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm font-medium text-blue-900 mb-1">
                  {selectedRole.roleName}
                </p>
                <p className="text-xs text-blue-700 mb-2">
                  {selectedRole.roleDescription || "No description available"}
                </p>
                <div className="text-xs text-blue-600">
                  <span className="font-medium">Permissions: </span>
                  {selectedRole.adminRolePermissions.length > 0 ? (
                    <span>{selectedRole.adminRolePermissions.join(", ")}</span>
                  ) : (
                    <span className="text-gray-500">
                      No permissions assigned
                    </span>
                  )}
                </div>
              </div>
            )}

            {errors.roleReference && (
              <p className="mt-1 text-sm text-red-600">
                {errors.roleReference}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={isLoading || !isFormValid()}
              className={`w-full py-3 px-4 flex justify-center items-center rounded-lg text-white font-medium transition-all ${
                isLoading || !isFormValid()
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700 shadow-lg hover:shadow-blue-500/30"
              }`}
            >
              {isLoading ? (
                <>
                  <Loader className="animate-spin h-5 w-5 mr-2" />
                  Creating Profile...
                </>
              ) : (
                "Create Profile"
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default AdminProfileForm;
